import { HttpClient } from 'api/http-client';
import {
  AddMembersRequestPayload,
  AddMembersResponse,
  AddonsMapping,
  ChangePlanEstimateResponse,
  ChangePlanRequestPayload,
  ChangePlanResponse,
  DownloadInvoiceResponse,
  InvoiceDetails,
  InvoiceListResponse,
  PortalSession,
  Subscription,
  AgentEstimateWithAddonPayload,
  AddonsUsage,
} from './types';

class BillingHttpClient extends HttpClient {
  public getSubscription(includeMeta: boolean): Promise<Subscription> {
    return this.get(`/subscription${includeMeta ? '?includeMeta=true' : ''}`);
  }

  public getInvoiceDetails(): Promise<InvoiceDetails> {
    return this.get('/subscription/invoice-details');
  }

  public getInvoiceList(subscriptionId: number): Promise<InvoiceListResponse> {
    return this.get(`/subscription/${subscriptionId}/invoices?limit=10`);
  }

  public createPortalSession(): Promise<PortalSession> {
    return this.post('/subscription/self-serve');
  }

  public changePlan(
    payload: ChangePlanRequestPayload,
  ): Promise<ChangePlanResponse> {
    return this.post('/subscription/change-plan', payload);
  }

  public changePlanEstimate(
    payload: ChangePlanRequestPayload,
  ): Promise<ChangePlanEstimateResponse> {
    return this.post('/subscription/change-plan?estimate=true', payload);
  }

  public addLicences(count: number): Promise<Subscription> {
    return this.post(`/subscription/add-licences/${count}`);
  }

  public addLicenceAddon(
    count: number,
    addonIdentifier: AddonsMapping,
  ): Promise<Subscription> {
    return this.post(
      `/subscription/add-addons/${addonIdentifier}?count=${count}`,
    );
  }

  public addLicencesEstimate(
    count: number,
  ): Promise<ChangePlanEstimateResponse> {
    return this.post(`/subscription/add-licences/${count}?estimate=true`);
  }

  public addLicenceAddonEstimate(
    count: number,
    addonIdentifier: AddonsMapping,
  ): Promise<ChangePlanEstimateResponse> {
    return this.post(
      `/subscription/add-addons/${addonIdentifier}?${
        count > 0 ? `count=${count}&` : ''
      }estimate=true`,
    );
  }

  public addMembers(
    payload: AgentEstimateWithAddonPayload,
  ): Promise<AddMembersResponse> {
    const { addonIdentifier } = payload;

    return this.post(
      `/subscription/add-agents?${
        addonIdentifier ? `addons=${addonIdentifier}` : ''
      }`,
      payload.data,
    );
  }

  public addMembersAddon(
    data: AddMembersRequestPayload,
    addonIdentifier?: string,
  ): Promise<AddMembersResponse> {
    return this.put(`/subscription/add-addon-agents/${addonIdentifier}`, [
      {
        user: data,
      },
    ]);
  }

  public estimateAddMembersAddon(
    data: AddMembersRequestPayload,
    addonIdentifier?: string,
  ): Promise<ChangePlanEstimateResponse> {
    return this.put(
      `/subscription/add-addon-agents/${addonIdentifier}?estimate=true`,
      [
        {
          user: data,
        },
      ],
    );
  }

  public addMembersEstimate(
    payload: AgentEstimateWithAddonPayload,
  ): Promise<ChangePlanEstimateResponse> {
    const { addonIdentifier } = payload;

    return this.post(
      `/subscription/add-agents?estimate=true${
        addonIdentifier ? `&addons=${addonIdentifier}` : ''
      }`,
      payload.data,
    );
  }

  public addPartnerMembersAndInviteToProject(
    payload: AddMembersRequestPayload,
    projectId: number,
  ): Promise<AddMembersResponse> {
    return this.post(
      `/subscription/add-partner-agents?projectId=${projectId}`,
      payload,
    );
  }

  public addPartnerMembers(
    payload: AddMembersRequestPayload,
  ): Promise<AddMembersResponse> {
    return this.post('/subscription/add-partner-agents', payload);
  }

  public addPartnerMembersEstimate(
    payload: AgentEstimateWithAddonPayload,
  ): Promise<ChangePlanEstimateResponse> {
    return this.post(
      '/subscription/add-partner-agents?estimate=true',
      payload.data,
    );
  }

  public changePlanCheckout(
    payload: ChangePlanRequestPayload,
  ): Promise<ChangePlanResponse> {
    return this.post('/subscription/change-plan-checkout', payload);
  }

  public getAddonUsageDetails({ feature }): Promise<AddonsUsage> {
    return this.get(`/feature-usage/${feature}`);
  }

  public changeAddonCheckout(
    count: number,
    addonIdentifier: AddonsMapping,
  ): Promise<ChangePlanResponse> {
    const payload = {
      units: count,
    };

    return this.post(
      `/subscription/change-addon-checkout/${addonIdentifier}`,
      payload,
    );
  }

  public reactivationCheckout(
    payload: ChangePlanRequestPayload,
  ): Promise<ChangePlanResponse> {
    return this.post(
      '/subscription/change-plan-checkout?reactivate=true',
      payload,
    );
  }

  public removeScheduledPlanChange(): Promise<void> {
    return this.post('/subscription/remove-plan-changes');
  }

  public scheduleSubscriptionCancellation(formValues): Promise<void> {
    return this.post('/subscription/schedule-cancellation', formValues);
  }

  public removeScheduledCancellation(): Promise<void> {
    return this.post('/subscription/remove-cancellation');
  }

  public updateInvoiceDetails(
    payload: Partial<InvoiceDetails>,
  ): Promise<InvoiceDetails> {
    return this.post('/subscription/invoice-details', payload);
  }

  public downloadInvoice(
    subscriptionId: number,
    invoiceId: number,
  ): Promise<DownloadInvoiceResponse> {
    return this.get(
      `subscription/${subscriptionId}/invoice/${invoiceId}/download`,
    );
  }

  public forceSyncSubscription(subscriptionId: number): Promise<void> {
    return this.post(`/subscription/${subscriptionId}/force-sync`);
  }

  public reactivateMember(userId: number): Promise<AddMembersResponse> {
    return this.post(`/subscription/activate-member/${userId}`);
  }

  public reactivateMemberEstimate(
    userId: number,
  ): Promise<ChangePlanEstimateResponse> {
    return this.post(`/subscription/activate-member/${userId}?estimate=true`);
  }

  public reactivatePartnerMember(userId: number): Promise<AddMembersResponse> {
    return this.post(`/subscription/activate-partner-member/${userId}`);
  }

  public reactivatePartnerMemberEstimate(
    userId: number,
  ): Promise<ChangePlanEstimateResponse> {
    return this.post(
      `/subscription/activate-partner-member/${userId}?estimate=true`,
    );
  }
}

export const BillingAPI = new BillingHttpClient();
